import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { formatDate, formatDateRange } from "@/lib/format";

/**
 * A menu as the Menus pages read it (POS2-1456): one site's view, where the live switch is that
 * site's `menu_sites.is_active` and "today" is the site's date (`useSiteToday`).
 */

export type Cadence = "always_on" | "cycle" | "one_off";

export interface MenuSite {
  site_id: string;
  site_name: string;
  is_active: boolean;
  /** `YYYY-MM-DD` it goes live here while it waits, off, for its start date. */
  live_from: string | null;
}

export interface Menu {
  id: string;
  name: string;
  display_name: string | null;
  cadence: Cadence;
  starts_on: string | null;
  ends_on: string | null;
  daypart_start: string | null;
  daypart_end: string | null;
  successor_id: string | null;
  /** Shared by menus read out of one file, which run together. */
  split_group: string | null;
  file_name: string;
  file_type: string;
  status: string;
  error_message: string | null;
  /** The last dish read when the file was too long to read in full. */
  cut_off_after: string | null;
  /** The list only: files being read into this menu as parts. */
  parts_reading: string[];
  archived_at: string | null;
  created_at: string;
  created_by_name: string | null;
  recipe_count: number;
  catalogue_name: string | null;
  sites: MenuSite[];
}

/** Where generating a recipe's ingredients stands. */
export type ProcessingStatus = "pending" | "processing" | "ready" | "failed";

/** One recipe on a menu, as GET /menus/v2/{id}?site_id= lists it for that site. */
export interface MenuRecipe {
  id: string;
  name: string;
  meal_period: string | null;
  processing_status: ProcessingStatus;
  processing_error: string | null;
  recipe_category_id: string | null;
  category_slug: string | null;
  ingredient_count: number;
  unmatched_ingredient_count: number;
  cost_per_portion: number | null;
  portion_weight_kg: number | null;
  planned_covers: number | null;
  /** Another menu running at the site with a dish of the same name, while this one runs too. */
  also_live_in: string | null;
  /** Edited on the recipe page, added by hand, or given covers by hand: Re-extract replaces it. */
  changed_by_hand: boolean;
}

/** A further file of a menu, added because it had the same type and dates. */
export interface MenuPart {
  id: string;
  file_name: string;
  status: "reading" | "read" | "failed";
  error_message: string | null;
  /** The last dish read when the file was too long to read in full. */
  cut_off_after: string | null;
}

export interface MenuDetail extends Menu {
  recipes: MenuRecipe[];
  parts: MenuPart[];
  /** The other menus read out of the same file. */
  split_with: { id: string; name: string; display_name: string | null }[];
}

/** The upload's menu: a new one, or the one the file was added to as a part. */

/** One dish at a site, as All recipes lists it: its recipe rows on the site's menus, shown as one. */
export interface Dish {
  key: string;
  /** The row the dish shows and opens: one on a running menu, else the newest menu's. */
  recipe_id: string;
  name: string;
  processing_status: ProcessingStatus;
  category_slug: string | null;
  cost_per_portion: number | null;
  portion_weight_kg: number | null;
  unmatched_ingredient_count: number;
  menu_ids: string[];
}

export const CADENCE_LABELS: Record<Cadence, string> = {
  always_on: "menus.cadence.always_on",
  cycle: "menus.cadence.cycle",
  one_off: "menus.cadence.one_off",
};

export type MenuGroup = Cadence | "ended";

export const GROUPS: MenuGroup[] = ["always_on", "cycle", "one_off", "ended"];

export function hasEnded(menu: Menu, today: string): boolean {
  return menu.ends_on !== null && menu.ends_on < today;
}

export function inDates(menu: Menu, today: string): boolean {
  return menu.starts_on === null || (menu.starts_on <= today && today <= menu.ends_on!);
}

/** An ended cycle waiting for its turn stays with the cycles: it runs again (POS2-1469). */
export function groupOf(menu: Menu, menus: Menu[], today: string): MenuGroup {
  return hasEnded(menu, today) && !waitsForTurn(menu, menus, today) ? "ended" : menu.cadence;
}

/** The menu's row at `siteId`. The list is site-scoped, so every menu on it is on that site. */
function siteRow(menu: Menu, siteId: string): MenuSite {
  const site = menu.sites.find((s) => s.site_id === siteId);
  if (!site) throw new Error(`menu ${menu.id} is not on site ${siteId}`);
  return site;
}

/** The live switch at `siteId`. */
export function isLiveAt(menu: Menu, siteId: string): boolean {
  return siteRow(menu, siteId).is_active;
}

/** The day the menu goes live at `siteId` while it waits for its start date, else null. */
export function liveFromAt(menu: Menu, siteId: string): string | null {
  return siteRow(menu, siteId).live_from;
}

/** Switched on and not ended: what Scrappy can match. */
export function runsAt(menu: Menu, siteId: string, today: string): boolean {
  return isLiveAt(menu, siteId) && !hasEnded(menu, today);
}

/** Where a menu sorts in a dish's list of menus: running, then waiting, then ended. */
export function menuRank(menu: Menu, siteId: string, today: string): number {
  if (runsAt(menu, siteId, today)) return 0;
  return hasEnded(menu, today) ? 2 : 1;
}

/** Recipes are being read from the file: the row spins and the page polls. */
export function isReading(menu: Menu): boolean {
  return menu.status === "extracting" || menu.status === "processing";
}

export function predecessorOf(menu: Menu, menus: Menu[]): Menu | undefined {
  return menus.find((m) => m.successor_id === menu.id);
}

/** A rotation's turn: up its "Comes after" chain is a menu that has not ended, and when that hands
 * over, the rollover moves this one's dates on and runs it again (POS2-1469). Not once every menu
 * on a loop has ended: a stalled rotation is ended. */
export function waitsForTurn(menu: Menu, menus: Menu[], today: string): boolean {
  const seen = new Set([menu.id]);
  let m = predecessorOf(menu, menus);
  while (m && !seen.has(m.id)) {
    if (!hasEnded(m, today)) return true;
    seen.add(m.id);
    m = predecessorOf(m, menus);
  }
  return false;
}

/** Choosing `predecessor` for the menu `selfId` closes a rotation: `predecessor` already comes
 * after it, directly or down a chain (POS2-1469). */
export function closesLoop(predecessor: Menu, selfId: string, menus: Menu[]): boolean {
  const seen = new Set<string>();
  let m: Menu | undefined = predecessor;
  while (m && !seen.has(m.id)) {
    if (m.id === selfId) return true;
    seen.add(m.id);
    m = predecessorOf(m, menus);
  }
  return false;
}

export function shortTime(value: string): string {
  return value.slice(0, 5);
}

export function serviceTime(menu: Menu): string | null {
  if (!menu.daypart_start || !menu.daypart_end) return null;
  return `${shortTime(menu.daypart_start)}–${shortTime(menu.daypart_end)}`;
}

export function shortDate(locale: Locale, iso: string): string {
  return formatDate(locale, iso, { day: "numeric", month: "short", timeZone: "UTC" });
}

export function datesLabel(locale: Locale, menu: Menu): string | null {
  if (!menu.starts_on || !menu.ends_on) return null;
  if (menu.starts_on === menu.ends_on) return shortDate(locale, menu.starts_on);
  return formatDateRange(locale, menu.starts_on, menu.ends_on);
}

/** The name the user chose, else "Menu <dates>", else the uploaded file's name. */
export function menuLabel(t: TFunction, locale: Locale, menu: Menu): string {
  if (menu.display_name) return menu.display_name;
  const dates = datesLabel(locale, menu);
  return dates ? t("menus.list.unnamedMenu", { dates }) : menu.name;
}

/** "Uploaded <date> by <name> from <file>", without the name when the uploader is unknown. */
export function uploadLine(t: TFunction, locale: Locale, menu: Menu): string {
  const date = shortDate(locale, menu.created_at.slice(0, 10));
  return menu.created_by_name
    ? t("menus.settings.uploadedBy", { date, name: menu.created_by_name, file: menu.file_name })
    : t("menus.settings.uploaded", { date, file: menu.file_name });
}

/** Why a recipe's cost is missing or estimated, or null when every ingredient is matched. A recipe
 * whose ingredients were never generated has none unmatched, so it must not read as matched. */
export function costWarning(
  t: TFunction,
  recipe: { processing_status: ProcessingStatus; unmatched_ingredient_count: number },
): string | null {
  switch (recipe.processing_status) {
    case "pending":
    case "processing":
      return t("menus.detail.noIngredients");
    case "failed":
      return t("menus.detail.ingredientsFailed");
    case "ready":
      if (recipe.unmatched_ingredient_count === 0) return null;
      return t("menus.detail.noMatch", { count: recipe.unmatched_ingredient_count });
  }
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

/** Always on by service start, cycle and one-off by start date, ended most recent first. */
export function sortGroup(group: MenuGroup, menus: Menu[]): Menu[] {
  const key = (m: Menu): string => {
    if (group === "always_on") return m.daypart_start ?? "";
    if (group === "ended") return m.ends_on!;
    return m.starts_on ?? "9999";
  };
  const sorted = [...menus].sort((a, b) => key(a).localeCompare(key(b)));
  return group === "ended" ? sorted.reverse() : sorted;
}
